import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ActivityLogToken, DataSourceLoadOptions } from '@sleed/bizcommerce-api';
import { activityLogsApi } from '~/lib/api/client';
import { applySort, type SortDir } from '~/lib/grid/sort';

export interface ActivityLogListParams {
	skip?: number;
	take?: number;
	/** Debounced free-text search over actor, entity name, business key and display name. */
	search?: string;
	/** Action type filter (ActivityAction value), or null for all. */
	action?: number | null;
	/**
	 * Role filter on `ActorRoleId` — the id, not the name, so renaming a role neither breaks a saved
	 * filter nor splits one role's history in two.
	 */
	actorRoleId?: number | null;
	/**
	 * Lower bound on `OccurredOnUtc` as a UTC ISO string, or null for no bound. A rolling window measured
	 * back from "now" rather than a calendar day, so there is no midnight boundary to reconcile between the
	 * viewer's local day and the UTC timestamps the log stores.
	 */
	fromDateUtc?: string | null;
	sortSelector?: string | null;
	sortDir?: SortDir;
}

export interface ActivityLogListResult {
	items: ActivityLogToken[];
	totalCount: number;
}

export const activityLogKeys = {
	all: ['activity-log'] as const,
	list: (params: unknown) => ['activity-log', 'list', params] as const
};

/** Token json names the server filters/sorts on (see ActivityLogToken). */
export const activityLogFields = {
	occurredOn: 'occ',
	action: 'act',
	actorName: 'anm',
	actorRoleId: 'arid',
	actorRoleName: 'arnm',
	entityName: 'enm',
	entityKey: 'ekey',
	entityDisplayName: 'edn'
} as const;

function buildLoadOptions(params: ActivityLogListParams): DataSourceLoadOptions {
	const {
		skip = 0,
		take = 25,
		search = '',
		action = null,
		actorRoleId = null,
		fromDateUtc = null,
		sortSelector = null,
		sortDir = 'desc'
	} = params;
	const loadOptions: DataSourceLoadOptions = { skip, take, requireTotalCount: true };

	const filters: unknown[] = [];
	const term = search.trim();
	if (term)
		filters.push([
			[activityLogFields.actorName, 'contains', term],
			'or',
			[activityLogFields.entityName, 'contains', term],
			'or',
			[activityLogFields.entityKey, 'contains', term],
			'or',
			[activityLogFields.entityDisplayName, 'contains', term]
		]);
	if (action !== null) filters.push([activityLogFields.action, '=', action]);
	if (actorRoleId !== null) filters.push([activityLogFields.actorRoleId, '=', actorRoleId]);
	if (fromDateUtc) filters.push([activityLogFields.occurredOn, '>=', fromDateUtc]);

	// The DevExtreme server binder parses `filter`/`sort` from JSON STRINGS: the generated client
	// flattens a real array into repeated `filter=` params, which the binder cannot read (same
	// contract as use-loyalty-products / use-orders-list).
	if (filters.length > 0)
		(loadOptions as { filter?: unknown }).filter = JSON.stringify(filters.length === 1 ? filters[0] : joinWithAnd(filters));

	if (sortSelector) applySort(loadOptions, sortSelector, sortDir);

	return loadOptions;
}

/** `[a, 'and', b, 'and', c]` — the DevExtreme filter grammar for combining criteria. */
function joinWithAnd(filters: unknown[]): unknown[] {
	return filters.flatMap((filter, index) => (index === 0 ? [filter] : ['and', filter]));
}

/**
 * Server-paged admin activity log (BW-4412) via `activityLogsApi.getAll` (DevExtreme loadOptions
 * envelope). Server-paged rather than client-paged because the table grows without bound — the
 * endpoint also caps `take`, so asking for everything is not an option.
 *
 * `keepPreviousData` keeps the current page visible while the next page/filter loads.
 */
export function useActivityLogList(params: ActivityLogListParams = {}) {
	return useQuery<ActivityLogListResult>({
		queryKey: activityLogKeys.list(params),
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const res = (await activityLogsApi.getAll(buildLoadOptions(params))).data;
			return { items: res.data ?? [], totalCount: res.totalCount ?? 0 };
		}
	});
}
